function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import Downshift from "downshift";
import classNames from "classnames";
import { useRect } from "@reach/rect";
import { noop, attempt, isError, isEmpty } from "lodash-es";
import { Search, Close } from "../../icons";
import Input from "../../forms/input/Input";
import { onEnter as onEnterHelper } from "../../helpers";
import { Label } from "../label/Label";
export const MAX_OPTIONS_HEIGHT = 254;
const INPUT_HEIGHT = 38;
const BORDER_HEIGHT = 1;
export const MAX_RESULTS_BOX_HEIGHT = MAX_OPTIONS_HEIGHT + INPUT_HEIGHT + BORDER_HEIGHT * 2;
const SearchSuggestionSelect = _ref => {
  let {
    value,
    options,
    inputValue,
    prefixClassName,
    maxHeight,
    onEnter,
    size,
    filterOptions,
    required,
    error,
    ...props
  } = _ref;
  const [menuIsOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const optionsRef = useRef();
  const rect = useRect(ref) || {};
  const selectedItem = value !== undefined && value !== null && value !== "" ? options.find(e => e.value === value) : {};
  function onInputValueChange(val) {
    if (val !== inputValue) {
      props.onInputValueChange(val);
    }
  }
  useLayoutEffect(() => {
    const inputMargin = 4;
    let styles = {
      top: `${rect.top + rect.height + inputMargin}px`,
      left: `${rect.left}px`,
      right: `${rect.right}px`,
      width: `${rect.width}px`,
      minHeight: `${rect.height}px`,
      transform: null,
      position: "fixed",
      height: null
    };
    const collidesWithBottom = window.innerHeight <= rect.bottom + MAX_RESULTS_BOX_HEIGHT;
    if (collidesWithBottom) {
      // when options' rect extends past the bottom of the window move it above the input
      styles.transform = `translateY(-100%) translateY(-${rect.height + inputMargin * 2}px)`;
      styles.height = `${MAX_OPTIONS_HEIGHT}px`;
    }
    Object.entries(styles).forEach(_ref2 => {
      let [key, value] = _ref2;
      optionsRef.current.style[key] = value;
    });
  }, [rect.top, rect.left, rect.right, rect.width, rect.height]);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: `${prefixClassName}-wrapper search-select ${size}`
  }, /*#__PURE__*/React.createElement(Downshift, {
    itemToString: item => {
      return item ? item.label : "";
    },
    isOpen: menuIsOpen,
    selectedItem: selectedItem,
    onStateChange: changes => {
      if (changes !== null && changes !== void 0 && changes.selectedItem) {
        props.onChange(changes.selectedItem);
      }
      if (!menuIsOpen && changes.inputValue) {
        setIsOpen(true);
      }
    },
    onSelect: () => {
      setIsOpen(false);
      document.activeElement.blur();
    },
    onInputValueChange: value => {
      if (value !== undefined && !Object.is(inputValue, value)) {
        onInputValueChange(value || "");
      }
    },
    stateReducer: (state, changes) => {
      const {
        highlightedIndex
      } = changes;
      switch (changes.type) {
        case Downshift.stateChangeTypes.keyDownArrowDown:
        case Downshift.stateChangeTypes.keyDownArrowUp:
          {
            const inputValue = attempt(() => {
              const option = ref.current.querySelectorAll("li")[highlightedIndex];
              if (option) {
                option.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest"
                });
              }
              return option ? option.innerText : state.inputValue;
            });
            if (isError(inputValue)) return changes;
            return {
              ...changes,
              inputValue
            };
          }
        default:
          return changes;
      }
    }
  }, _ref3 => {
    let {
      getInputProps,
      getItemProps,
      getMenuProps,
      isOpen,
      clearSelection,
      getLabelProps
    } = _ref3;
    function onClearInput(e) {
      e.stopPropagation();
      clearSelection();
      props.onChange({});
      onInputValueChange("");
      setIsOpen(false);
    }
    function getOptions(options) {
      if (!filterOptions) return options;
      let filteredOptions = options.filter(o => o.label.toLowerCase().includes(inputValue.toLowerCase()));
      return filteredOptions;
    }
    let itemIndex = 0;
    function renderOptions(options) {
      const optionsToRender = getOptions(options);
      if (optionsToRender.length === 0) return /*#__PURE__*/React.createElement("div", {
        className: "no-results"
      }, props.noResultsText);
      return optionsToRender.map(item => /*#__PURE__*/React.createElement("li", getItemProps({
        key: item.value,
        index: itemIndex++,
        item,
        className: classNames(`${prefixClassName}-options-item`, {
          selected: item.value === selectedItem.value
        })
      }), item.label));
    }
    return /*#__PURE__*/React.createElement("div", _extends({
      style: {
        position: "relative"
      }
      /**
       * Fixes multiple labels bug.
       * Downshift automatically adds the aria-labelledby prop if it does not exist.
       * */
    }, props.label ? {
      "aria-labelledby": undefined
    } : {}), props.label && /*#__PURE__*/React.createElement(Label, _extends({}, getLabelProps(), {
      required: required
    }), props.label), /*#__PURE__*/React.createElement(Input, {
      size: size,
      icon: {
        position: "left",
        render: () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Search, null), (inputValue === null || inputValue === void 0 ? void 0 : inputValue.length) > 0 && /*#__PURE__*/React.createElement(Close, {
          className: "close-icon",
          role: "button",
          tabIndex: "0",
          onKeyPress: onEnterHelper(onClearInput),
          onClick: onClearInput,
          style: {
            right: 0,
            left: "unset"
          }
        }))
      },
      disabled: props.disabled,
      onClick: () => setIsOpen(true),
      onBlur: () => setIsOpen(false),
      value: inputValue || "",
      className: classNames({
        "has-error": error
      }),
      inputProps: getInputProps({
        placeholder: props.placeholder,
        className: classNames("texada-ui-dropdown", {
          "is-open": isOpen
        }),
        disabled: props.disabled,
        onKeyPress: onEnterHelper(() => {
          setIsOpen(false);
          onEnter();
        })
      })
    }), /*#__PURE__*/React.createElement("div", {
      ref: optionsRef,
      className: classNames(`${prefixClassName}-options`)
    }, isOpen && !props.disabled && /*#__PURE__*/React.createElement("ul", _extends({}, getMenuProps(), {
      style: {
        maxHeight
      }
    }), /*#__PURE__*/React.createElement(React.Fragment, null, Array.isArray(options) ? renderOptions(options) : Object.entries(options).map(_ref4 => {
      let [categoryLabel, options] = _ref4;
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: categoryLabel
      }, /*#__PURE__*/React.createElement("span", {
        className: `${prefixClassName}-category`
      }, categoryLabel), renderOptions(options));
    })))));
  }), error && !isEmpty(error) ? /*#__PURE__*/React.createElement("div", {
    className: "tui-invalid-feedback",
    children: error
  }) : "");
};
SearchSuggestionSelect.displayName = "SearchSuggestionSelect";
SearchSuggestionSelect.defaultProps = {
  label: undefined,
  placeholder: "",
  disabled: false,
  noResultsText: "No results found.",
  prefixClassName: "tui-select",
  options: [],
  value: "",
  style: {},
  maxHeight: 200,
  onChange: noop,
  onEnter: noop,
  size: "md",
  filterOptions: true
};
SearchSuggestionSelect.propTypes = {
  options: PropTypes.any,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  maxHeight: PropTypes.number,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  prefixClassName: PropTypes.string,
  onChange: PropTypes.func,
  inputValue: PropTypes.string.isRequired,
  onInputValueChange: PropTypes.func.isRequired,
  noResultsText: PropTypes.string,
  disabled: PropTypes.bool,
  onEnter: PropTypes.func,
  size: PropTypes.string,
  filterOptions: PropTypes.bool
};
export default SearchSuggestionSelect;
//# sourceMappingURL=SearchSuggestionSelect.js.map